import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { CmsLink as Link, EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [specialtyOpen, setSpecialtyOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const { editing } = useCmsContent();

  // Timers for delayed close
  const servicesTimer = React.useRef(null);
  const specialtyTimer = React.useRef(null);
  const moreTimer = React.useRef(null);

  const visiblePath = editing
    ? location.pathname.replace(/^\/admin\/edit/, '') || '/'
    : location.pathname;
  const isActive = (path) => visiblePath === path;

  const handleAuthLink = (event, closeMobileMenu = false) => {
    if (editing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (closeMobileMenu) {
      setIsOpen(false);
    }
  };

  const handleServicesEnter = () => {
    if (servicesTimer.current) clearTimeout(servicesTimer.current);
    setServicesOpen(true);
  };

  const handleServicesLeave = () => {
    servicesTimer.current = setTimeout(() => setServicesOpen(false), 300);
  };

  const handleSpecialtyEnter = () => {
    if (specialtyTimer.current) clearTimeout(specialtyTimer.current);
    setSpecialtyOpen(true);
  };

  const handleSpecialtyLeave = () => {
    specialtyTimer.current = setTimeout(() => setSpecialtyOpen(false), 300);
  };

  const handleMoreEnter = () => {
    if (moreTimer.current) clearTimeout(moreTimer.current);
    setMoreOpen(true);
  };

  const handleMoreLeave = () => {
    moreTimer.current = setTimeout(() => setMoreOpen(false), 300);
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-brand-700 rounded flex items-center justify-center">
              <EditableText contentKey="shared.nav.logoLetter" className="text-white text-2xl font-bold" multiline={false} />
            </div>
            <div>
              <p className="text-xs text-gray-500"><EditableText contentKey="shared.nav.tagline" multiline={false} /></p>
              <h1 className="text-xl font-light text-gray-800"><EditableText contentKey="shared.nav.brandName" multiline={false} /></h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-5">
            <Link
              to="/"
              className={`text-gray-700 hover:text-brand-700 transition-colors ${
                isActive('/') ? 'text-brand-700 font-medium' : ''
              }`}
            >
              <EditableText contentKey="shared.nav.home" multiline={false} />
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative group"
              onMouseEnter={handleServicesEnter}
              onMouseLeave={handleServicesLeave}
            >
              <button className="flex items-center text-gray-700 hover:text-brand-700 transition-colors">
                <EditableText contentKey="shared.nav.services" multiline={false} /> <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {servicesOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-md py-2"
                  onMouseEnter={handleServicesEnter}
                  onMouseLeave={handleServicesLeave}
                >
                  <Link
                    to="/services/individual-therapy"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.individualTherapy" multiline={false} />
                  </Link>
                  <Link
                    to="/services/couples-counseling"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.couplesCounseling" multiline={false} />
                  </Link>
                  <Link
                    to="/services/family-therapy"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.familyTherapy" multiline={false} />
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/book-appointment"
              className={`text-gray-700 hover:text-brand-700 transition-colors ${
                isActive('/book-appointment') ? 'text-brand-700 font-medium' : ''
              }`}
            >
              <EditableText contentKey="shared.nav.bookAppointment" multiline={false} />
            </Link>

            {/* Specialty Dropdown */}
            <div
              className="relative group"
              onMouseEnter={handleSpecialtyEnter}
              onMouseLeave={handleSpecialtyLeave}
            >
              <button className="flex items-center text-gray-700 hover:text-brand-700 transition-colors">
                <EditableText contentKey="shared.nav.specialty" multiline={false} /> <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {specialtyOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-md py-2 max-h-96 overflow-y-auto"
                  onMouseEnter={handleSpecialtyEnter}
                  onMouseLeave={handleSpecialtyLeave}
                >
                  <Link
                    to="/specialty/anxiety-treatment"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.anxietyTreatment" multiline={false} />
                  </Link>
                  <Link
                    to="/specialty/anger-management"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.angerManagement" multiline={false} />
                  </Link>
                  <Link
                    to="/specialty/ptsd"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.ptsd" multiline={false} />
                  </Link>
                  <Link
                    to="/specialty/depression"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.depression" multiline={false} />
                  </Link>
                  <Link
                    to="/specialty/adhd"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.adhd" multiline={false} />
                  </Link>
                  <Link
                    to="/specialty/eating-disorder"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.eatingDisorder" multiline={false} />
                  </Link>
                  <Link
                    to="/specialty/loss-and-grief"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.lossAndGrief" multiline={false} />
                  </Link>
                  <Link
                    to="/specialty/divorce-counseling"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.divorceCounseling" multiline={false} />
                  </Link>
                </div>
              )}
            </div>

            {/* More Dropdown */}
            <div
              className="relative group"
              onMouseEnter={handleMoreEnter}
              onMouseLeave={handleMoreLeave}
            >
              <button className="flex items-center text-gray-700 hover:text-brand-700 transition-colors">
                <EditableText contentKey="shared.nav.more" multiline={false} /> <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {moreOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-md py-2"
                  onMouseEnter={handleMoreEnter}
                  onMouseLeave={handleMoreLeave}
                >
                  <Link
                    to="/therapists"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.therapists" multiline={false} />
                  </Link>
                  <Link
                    to="/blog"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.blog" multiline={false} />
                  </Link>
                  <Link
                    to="/insurance"
                    className="block px-4 py-2 text-gray-700 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <EditableText contentKey="shared.nav.insurance" multiline={false} />
                  </Link>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <a
                href="/auth/signup"
                onClick={(event) => handleAuthLink(event)}
                className="whitespace-nowrap rounded-md border border-brand-700 px-3 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50"
              >
                <EditableText contentKey="shared.nav.signUp" multiline={false} />
              </a>
              <a
                href="/auth/login"
                onClick={(event) => handleAuthLink(event)}
                className="whitespace-nowrap rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-800"
              >
                <EditableText contentKey="shared.nav.login" multiline={false} />
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 space-y-2">
            <Link
              to="/"
              className="block px-4 py-2 text-gray-700 hover:bg-brand-50"
              onClick={() => setIsOpen(false)}
            >
              <EditableText contentKey="shared.nav.home" multiline={false} />
            </Link>
            <div className="px-4 py-2">
              <p className="font-medium text-gray-900 mb-2"><EditableText contentKey="shared.nav.services" multiline={false} /></p>
              <Link
                to="/services/individual-therapy"
                className="block px-4 py-1 text-gray-700 hover:text-brand-700"
                onClick={() => setIsOpen(false)}
              >
                <EditableText contentKey="shared.nav.individualTherapy" multiline={false} />
              </Link>
              <Link
                to="/services/couples-counseling"
                className="block px-4 py-1 text-gray-700 hover:text-brand-700"
                onClick={() => setIsOpen(false)}
              >
                <EditableText contentKey="shared.nav.couplesCounseling" multiline={false} />
              </Link>
              <Link
                to="/services/family-therapy"
                className="block px-4 py-1 text-gray-700 hover:text-brand-700"
                onClick={() => setIsOpen(false)}
              >
                <EditableText contentKey="shared.nav.familyTherapy" multiline={false} />
              </Link>
            </div>
            <Link
              to="/book-appointment"
              className="block px-4 py-2 text-gray-700 hover:bg-brand-50"
              onClick={() => setIsOpen(false)}
            >
              <EditableText contentKey="shared.nav.bookAppointment" multiline={false} />
            </Link>
            <Link
              to="/therapists"
              className="block px-4 py-2 text-gray-700 hover:bg-brand-50"
              onClick={() => setIsOpen(false)}
            >
              <EditableText contentKey="shared.nav.therapists" multiline={false} />
            </Link>
            <Link
              to="/blog"
              className="block px-4 py-2 text-gray-700 hover:bg-brand-50"
              onClick={() => setIsOpen(false)}
            >
              <EditableText contentKey="shared.nav.blog" multiline={false} />
            </Link>
            <div className="mx-4 flex gap-2">
              <a
                href="/auth/signup"
                onClick={(event) => handleAuthLink(event, true)}
                className="flex-1 rounded-md border border-brand-700 px-4 py-2 text-center text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50"
              >
                <EditableText contentKey="shared.nav.signUp" multiline={false} />
              </a>
              <a
                href="/auth/login"
                onClick={(event) => handleAuthLink(event, true)}
                className="flex-1 rounded-md bg-brand-700 px-4 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-brand-800"
              >
                <EditableText contentKey="shared.nav.login" multiline={false} />
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
